import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RateLimitStatus {
  isLimited: boolean;
  attemptsCount: number;
  lockoutUntil: string | null;
  remainingAttempts: number;
}

export function useRateLimit() {
  const [isChecking, setIsChecking] = useState(false);
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatus | null>(null);

  const checkRateLimit = async (email: string): Promise<RateLimitStatus> => {
    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-rate-limit", {
        body: { email, action: "check" },
      });

      if (error) {
        console.error("Rate limit check error:", error);
        // Return safe default if check fails
        return { isLimited: false, attemptsCount: 0, lockoutUntil: null, remainingAttempts: 5 };
      }

      const status: RateLimitStatus = {
        isLimited: data.isLimited || false,
        attemptsCount: data.attemptsCount || 0,
        lockoutUntil: data.lockoutUntil || null,
        remainingAttempts: data.remainingAttempts ?? 5,
      };

      setRateLimitStatus(status);
      return status;
    } catch (error) {
      console.error("Rate limit check error:", error);
      return { isLimited: false, attemptsCount: 0, lockoutUntil: null, remainingAttempts: 5 };
    } finally {
      setIsChecking(false);
    }
  };

  const recordLoginAttempt = async (email: string, success: boolean): Promise<void> => {
    try {
      await supabase.functions.invoke("check-rate-limit", {
        body: { 
          email, 
          action: success ? "record_success" : "record_failure" 
        },
      });
    } catch (error) {
      console.error("Failed to record login attempt:", error);
    }
  };

  const getLockoutMessage = (): string | null => {
    if (!rateLimitStatus?.isLimited || !rateLimitStatus.lockoutUntil) {
      return null;
    }

    const lockoutTime = new Date(rateLimitStatus.lockoutUntil);
    const now = new Date();
    const diffMs = lockoutTime.getTime() - now.getTime();
    const diffMins = Math.ceil(diffMs / 60000);

    if (diffMins <= 0) {
      return null;
    }

    return `Too many failed attempts. Please try again in ${diffMins} minute${diffMins !== 1 ? 's' : ''}.`;
  };

  return {
    checkRateLimit,
    recordLoginAttempt,
    rateLimitStatus,
    isChecking,
    getLockoutMessage,
  };
}
